import React from 'react';
import { useStaticQuery, graphql, navigate } from 'gatsby';
import styled from 'styled-components';
import {
  brandColours,
  brandFonts,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
  visuallyHidden,
} from '../styles';
import { ArrowButton, Button, Container, Heading, Overline, Svg } from './ui';
import arrowIcon from '../images/arrow-icon.inline.svg';
import longArrowIcon from '../images/long-arrow-icon.inline.svg';

const StyledPlansOverview = styled.section`
  ${sectionPaddings()}
  background-color: ${standardColours.lightGrey};
`;

const StyledHeader = styled.header`
  margin: 0 auto 30px;
  max-width: 740px;
  text-align: center;

  ${minBreakpointQuery.small`
    margin-bottom: 40px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 50px;
  `}
`;

const StyledOverline = styled(Overline)`
  margin-bottom: 16px;

  ${minBreakpointQuery.small`
    margin-bottom: 18px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 20px;
  `}
`;

const StyledItems = styled.div`
  display: grid;
  gap: 15px;

  ${minBreakpointQuery.medium`
    grid-template-columns: repeat(3, 1fr);
  `}
`;

const StyledCard = styled.article`
  background-color: ${standardColours.white};
  padding: 30px 35px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
`;

const StyledTop = styled.div`
  position: relative;
  padding-bottom: 35px;
  border-bottom: 1px solid ${standardColours.grey};
`;

const StyledPopular = styled.div`
  position: absolute;
  display: flex;
  top: -40px;
  left: 60%;
  transform: translateX(-50%);
`;

const StyledPopularIcon = styled(Svg)`
  margin-top: 20px;
  margin-right: 10px;
`;

const StyledPopularText = styled.p`
  width: 55px;
  font-family: ${brandFonts.secondary};
`;

const StyledIntro = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledHeading = styled.h3`
  ${fontSize(22)};
  font-weight: ${fontWeights.regular};
  color: ${brandColours.quinary};
`;

const StyledArrow = styled.button`
  border: none;
  background: none;
  padding: 0;

  span {
    ${visuallyHidden()}
  }
`;

const StyledIcon = styled(Svg)`
  height: 26px;
  width: 26px;
  vertical-align: middle;
`;

const StyledPriceInfo = styled.div`
  margin-top: 10px;
`;

const StylePriceHeading = styled.p`
  ${fontSize(16)};
  font-weight: ${fontWeights.regular};
  line-height: 1;

  strong {
    ${fontSize(32)};
  }
`;

const StyledPriceSubheading = styled.p`
  margin-top: 25px;
  ${fontSize(18)};

  span {
    font-weight: ${fontWeights.bold};
  }
`;

const StyledBottom = styled.div`
  padding-top: 35px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const StyledFeaturedHeading = styled.p``;

const StyledHighlightedFeatures = styled.p`
  margin-top: 10px;
  line-height: 2;
  white-space: break-spaces;
  flex-grow: 1;
`;

const StyledViewAllFeatures = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin-top: 10px;
  text-align: left;
`;

const StyledVatText = styled.p`
  ${fontSize(12)};
`;

const StyledFooter = styled.footer`
  display: grid;
  margin-top: 30px;

  ${minBreakpointQuery.smedium`
    text-align: center;
  `}
`;

const StyledButton = styled(Button)`
  margin-left: auto;
  margin-right: auto;
`;

const StyledFeaturesButton = styled(ArrowButton)`
  margin-top: 30px;
  margin-left: auto;
  margin-right: auto;

  ${maxBreakpointQuery.smedium`
    display: block;
  `}
`;

const PlansOverview = ({ overline, heading, ctaLink, plansLink }) => {
  const {
    allDatoCmsPlan: { nodes },
  } = useStaticQuery(graphql`
    query PlansOverviewQuery {
      allDatoCmsPlan(sort: { position: ASC }, limit: 3) {
        nodes {
          name
          heading
          subheading
          highlightedPlanFeatures
          isMostPopular
        }
      }
    }
  `);

  return (
    <StyledPlansOverview>
      <Container>
        <StyledHeader>
          {overline && <StyledOverline>{overline}</StyledOverline>}
          <Heading>{heading}</Heading>
        </StyledHeader>
        {nodes.length > 0 && (
          <StyledItems>
            {nodes.map(
              (
                {
                  name,
                  heading,
                  subheading,
                  highlightedPlanFeatures,
                  isMostPopular,
                },
                id,
              ) => (
                <StyledCard key={id}>
                  <StyledTop>
                    {isMostPopular && (
                      <StyledPopular>
                        <StyledPopularIcon image={longArrowIcon} />
                        <StyledPopularText>Most popular</StyledPopularText>
                      </StyledPopular>
                    )}
                    <StyledIntro>
                      <StyledHeading>{name}</StyledHeading>
                      <StyledArrow
                        onClick={() =>
                          navigate(`${plansLink.pageUrl.slug}#plans`, {
                            state: { plan: name },
                          })
                        }
                      >
                        <span>View plan</span>
                        <StyledIcon image={arrowIcon} />
                      </StyledArrow>
                    </StyledIntro>
                    <StyledPriceInfo>
                      <StylePriceHeading
                        dangerouslySetInnerHTML={{
                          __html: heading.replace(/(<p>|<\/p>)/g, ''),
                        }}
                      />
                      {subheading && (
                        <StyledPriceSubheading
                          dangerouslySetInnerHTML={{
                            __html: subheading.replace(/(<p>|<\/p>)/g, ''),
                          }}
                        />
                      )}
                    </StyledPriceInfo>
                  </StyledTop>
                  <StyledBottom>
                    <StyledFeaturedHeading>
                      <strong>This package includes:</strong>
                    </StyledFeaturedHeading>
                    <StyledHighlightedFeatures>
                      {highlightedPlanFeatures}
                    </StyledHighlightedFeatures>
                    <StyledViewAllFeatures
                      onClick={() =>
                        navigate(`${plansLink.pageUrl.slug}#plans`, {
                          state: { plan: name },
                        })
                      }
                    >
                      <strong>View all features</strong>
                    </StyledViewAllFeatures>
                  </StyledBottom>
                </StyledCard>
              ),
            )}
            <StyledVatText>* All Prices Exclude VAT</StyledVatText>
          </StyledItems>
        )}
        <StyledFooter>
          <StyledButton to={ctaLink.pageUrl} alt={true}>
            {ctaLink.text}
          </StyledButton>
          <StyledFeaturesButton href={`${plansLink.pageUrl.slug}#plans`}>
            {plansLink.text}
          </StyledFeaturesButton>
        </StyledFooter>
      </Container>
    </StyledPlansOverview>
  );
};

export default PlansOverview;
